"use client";

import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { Treatment } from "@/lib/treatments-data";
import Header from "@/components/layout/header";
import Breadcrumbs from "@/components/Breadcrumbs";
import HeroSection from "./root-canal/hero-section";
import QuickOverviewSection from "./root-canal/quick-overview-section";
import WhatIsSection from "./root-canal/what-is-section";
import SignsSection from "./root-canal/signs-section";
import CandidateSection from "./root-canal/candidate-section";
import TreatmentStepsSection from "./root-canal/treatment-steps-section";
import AftercareSection from "./root-canal/aftercare-section";
import WhyChooseSection from "./root-canal/why-choose-section";
import PricingSection from "./root-canal/pricing-section";
import TestimonialsSection from "./root-canal/testimonials-section";
import FAQSection from "./root-canal/faq-section";
import FinalCTASection from "./shared/final-cta-section";
import StickyNavigation from "./shared/sticky-navigation";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import { useTranslation } from "@/hooks/useTranslation";

gsap.registerPlugin(ScrollTrigger);

interface RootCanalPageProps {
  treatment: Treatment;
}

export default function RootCanalPage({ treatment }: RootCanalPageProps) {
  const { t, locale } = useTranslation("root-canal");
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pageRef.current) return;

    // Initialize GSAP ScrollTrigger
    ScrollTrigger.refresh();

    // Clean up function to properly dispose of all GSAP animations
    return () => {
      // Kill all ScrollTrigger instances
      const triggers = ScrollTrigger.getAll();
      triggers.forEach((trigger) => {
        try {
          trigger.kill();
        } catch (error) {
          console.warn("Error killing ScrollTrigger:", error);
        }
      });

      // Clear ScrollTrigger's cached matchMedia
      ScrollTrigger.clearMatchMedia();

      // Kill all running GSAP tweens
      gsap.killTweensOf("*");

      // Clear GSAP contexts
      gsap.globalTimeline.clear();
    };
  }, []);

  const navigationItems = [
    { id: "hero", label: t("navigation.sections.overview") },
    { id: "what-is", label: t("navigation.sections.whatIs") },
    { id: "signs", label: t("navigation.sections.signs") },
    { id: "candidate", label: t("navigation.sections.assessment") },
    { id: "treatment-steps", label: t("navigation.sections.process") },
    { id: "aftercare", label: t("navigation.sections.recovery") },
    { id: "why-choose", label: t("navigation.sections.whyUs") },
    { id: "pricing", label: t("navigation.sections.pricing") },
    { id: "testimonials", label: t("navigation.sections.reviews") },
    { id: "faq", label: t("navigation.sections.faq") },
    { id: "final-cta", label: t("navigation.sections.bookNow") },
  ];

  return (
    <div ref={pageRef} className="min-h-screen bg-slate-50 dark:bg-navy-950 overflow-x-clip">
      <Header />

      {/* Treatment Header Section */}
      <section className="relative bg-gradient-to-b from-navy to-navy-950 border-b border-white/10 pt-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,rgba(210,161,56,0.08)_0%,transparent_60%)]" />
        <div className="container relative py-6">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-between mb-4">
            <Breadcrumbs />
            <Link
              href={`/${locale}/treatments/`}
              className="hidden md:flex items-center space-x-2 text-white/60 hover:text-gold transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t("navigation.backToTreatments")}</span>
            </Link>
          </div>
        </div>
      </section>

      <StickyNavigation sections={navigationItems} />

      <main className="relative">
        <HeroSection treatment={treatment} />
        <QuickOverviewSection />
        <WhatIsSection />
        <SignsSection />
        <CandidateSection />
        <TreatmentStepsSection />
        <AftercareSection />
        <WhyChooseSection />
        <PricingSection />
        <TestimonialsSection />
        <FAQSection />

        <FinalCTASection
          treatmentName={t("finalCta.treatmentName")}
          customHeading={
            <>
              {t("finalCta.heading.part1")}{" "}
              <span className="text-gold">
                {t("finalCta.heading.part2")}
              </span>
            </>
          }
          customSubheading={t("finalCta.subheading")}
          customBenefits={[
            t("finalCta.benefits.0"),
            t("finalCta.benefits.1"),
            t("finalCta.benefits.2"),
          ]}
        />
      </main>

    </div>
  );
}
